import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  generateTicks,
  calculateDomain,
} from "../components";
import { getChartFocusStyles, getChevronPosition } from "../utils";
import { ChartDataPoint } from "../types";

// Type alias for blood pressure data points with required systolic/diastolic properties
export type BloodPressureDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "systolic" | "diastolic">>;

export interface BloodPressureChartProps {
  data: BloodPressureDataPoint[];
}

type ViewMode = "total" | "systolic" | "diastolic";

// Scoring rules for blood pressure
export const getSystolicColor = (value: number): string => {
  if (value < 90) return themeColors.warning; // AMBER
  if (value >= 90 && value <= 120) return themeColors.success; // GREEN
  if (value >= 121 && value <= 130) return themeColors.warning; // AMBER
  return themeColors.error; // RED
};

export const getDiastolicColor = (value: number): string => {
  if (value < 60) return themeColors.warning; // AMBER
  if (value >= 60 && value <= 80) return themeColors.success; // GREEN
  if (value >= 81 && value <= 90) return themeColors.warning; // AMBER
  return themeColors.error; // RED
};

// -------- Main Chart --------
const BloodPressureChart: React.FC<BloodPressureChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("total");

  // Find the first selected point, or default to first data point
  const initialSelectedPoint =
    data.find((point) => point.isSelected) || data[0];

  const [selectedPoint, setSelectedPoint] =
    useState<BloodPressureDataPoint>(initialSelectedPoint);
  const [hoveredPoint, setHoveredPoint] =
    useState<BloodPressureDataPoint | null>(null);
  const hasAnimated = React.useRef(false);

  // Update selected point when data changes and a point has isSelected
  React.useEffect(() => {
    const newSelectedPoint = data.find((point) => point.isSelected);
    if (newSelectedPoint) {
      setSelectedPoint(newSelectedPoint);
    }
  }, [data]);

  const handlePointClick = (data: any) => {
    if (data && data.activePayload) {
      setSelectedPoint(data.activePayload[0].payload);
    }
  };

  const handleViewModeChange = (newMode: ViewMode) => {
    hasAnimated.current = true;
    setViewMode(newMode);
  };

  const activePoint = hoveredPoint || selectedPoint;
  const activeIndex = data.findIndex((d) => d.date === activePoint.date);

  // Calculate dynamic Y-axis domain based on data
  const allSystolicValues = data.map((d) => d.systolic);
  const allDiastolicValues = data.map((d) => d.diastolic);

  const systolicDomain = calculateDomain(allSystolicValues, 60, 160, 10);
  const diastolicDomain = calculateDomain(allDiastolicValues, 40, 120, 10);

  // Use appropriate domain based on view mode
  const yDomain =
    viewMode === "diastolic"
      ? diastolicDomain
      : viewMode === "systolic"
        ? systolicDomain
        : [
            Math.min(systolicDomain[0], diastolicDomain[0]),
            Math.max(systolicDomain[1], diastolicDomain[1]),
          ];

  // Determine important values based on view mode
  const importantValues =
    viewMode === "systolic"
      ? [90, 120, 130]
      : viewMode === "diastolic"
        ? [60, 81, 90]
        : [60, 81, 90, 120, 130];

  const yTicks = generateTicks(yDomain, 20, importantValues);

  // Reference ranges configuration
  const systolicRanges = [
    {
      label: t("HealthChart.bloodPressureChart.zone.high"),
      yValue: 160,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.elevated"),
      yValue: 148,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.normal"),
      yValue: 130,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.low"),
      yValue: 105,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  const diastolicRanges = [
    {
      label: t("HealthChart.bloodPressureChart.zone.high"),
      yValue: 118,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.elevated"),
      yValue: 105,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.normal"),
      yValue: 92,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.low"),
      yValue: 75,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  const referenceRanges =
    viewMode === "systolic" ? systolicRanges : diastolicRanges;

  // Calculate Y positions for legend labels based on last data point
  const lastDataPoint = data[data.length - 1];
  const bloodPressureChartHeight = 200;
  const bloodPressureChartWidth = 27;
  const yMargin = { top: 10, bottom: 10 };
  const plotHeight = bloodPressureChartHeight - yMargin.top - yMargin.bottom;
  const yRange = yDomain[1] - yDomain[0];
  const systolicYPosition =
    yMargin.top +
    plotHeight * (1 - (lastDataPoint.systolic - yDomain[0]) / yRange);
  const diastolicYPosition =
    yMargin.top +
    plotHeight * (1 - (lastDataPoint.diastolic - yDomain[0]) / yRange);

  return (
    <>
      <style>{getChartFocusStyles("blood-pressure-chart")}</style>
      <Box
        className="blood-pressure-chart"
        sx={{
          width: "100%",
          background: theme.palette.background.paper,
          borderRadius: 2.5,
          padding: 2,
          boxShadow: shadows.light,
          transition: "background-color 0.3s ease",
          outline: "none",
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontSize: 16,
            paddingBottom: 2,
          }}
        >
          {t("HealthChart.bloodPressureChart.title")}
        </Typography>

        {/* Tab Buttons */}
        <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
          {(["total", "systolic", "diastolic"] as ViewMode[]).map((mode) => (
            <Button
              key={mode}
              fullWidth
              onClick={() => handleViewModeChange(mode)}
              variant={viewMode === mode ? "contained" : "outlined"}
              sx={{
                paddingTop: convertPxToRem(6),
                paddingBottom: convertPxToRem(6),
                paddingLeft: convertPxToRem(10),
                paddingRight: convertPxToRem(10),
                background:
                  viewMode === mode
                    ? themeColors.primary
                    : theme.palette.grey[100],
                color: viewMode === mode ? "white" : theme.palette.grey[700],
              }}
            >
              {t(`HealthChart.bloodPressureChart.view.${mode}`)}
            </Button>
          ))}
        </Box>

        {/* Chart Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 200,
            outline: "none",
          }}
        >
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 10, right: 55, left: 0, bottom: 10 }}
              onClick={handlePointClick}
            >
              {/* Grid */}
              <CartesianGrid
                stroke={theme.palette.grey[300]}
                strokeDasharray="5 5"
                vertical={false}
                horizontal={viewMode !== "total"}
              />

              {/* Reference Lines and Zones for Systolic/Diastolic Views */}
              {viewMode === "systolic" && (
                <>
                  {/* Background zones */}
                  <ReferenceArea
                    y1={130}
                    y2={yDomain[1]}
                    fill={backgroundColors.errorLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={120}
                    y2={130}
                    fill={backgroundColors.warningLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={90}
                    y2={120}
                    fill={backgroundColors.successLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={yDomain[0]}
                    y2={90}
                    fill={backgroundColors.warningLight}
                    fillOpacity={0.5}
                  />

                  {/* Dashed lines */}
                  <ReferenceLine
                    y={130}
                    stroke={themeColors.error}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                  <ReferenceLine
                    y={120}
                    stroke={themeColors.warning}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                  <ReferenceLine
                    y={90}
                    stroke={themeColors.warning}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                </>
              )}

              {viewMode === "diastolic" && (
                <>
                  {/* Background zones for diastolic */}
                  <ReferenceArea
                    y1={90}
                    y2={yDomain[1]}
                    fill={backgroundColors.errorLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={81}
                    y2={90}
                    fill={backgroundColors.warningLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={60}
                    y2={81}
                    fill={backgroundColors.successLight}
                    fillOpacity={0.5}
                  />
                  <ReferenceArea
                    y1={yDomain[0]}
                    y2={60}
                    fill={backgroundColors.warningLight}
                    fillOpacity={0.5}
                  />

                  {/* Dashed lines */}
                  <ReferenceLine
                    y={90}
                    stroke={themeColors.error}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                  <ReferenceLine
                    y={81}
                    stroke={themeColors.warning}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                  <ReferenceLine
                    y={60}
                    stroke={themeColors.warning}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                </>
              )}

              {/* X Axis */}
              <XAxis
                dataKey="date"
                tick={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                dy={5}
              />

              {/* Y Axis */}
              <YAxis
                domain={yDomain}
                ticks={yTicks}
                tick={{
                  fontSize: 11,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                width={bloodPressureChartWidth}
              />

              {/* Tooltip with custom cursor for vertical dashed line - placed before Lines so it renders behind dots */}
              <Tooltip
                content={(props) => (
                  <CustomTooltipTracker
                    {...props}
                    setHoveredPoint={setHoveredPoint}
                  />
                )}
                cursor={{
                  stroke: theme.palette.grey[900],
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />

              {/* Systolic Line */}
              {viewMode !== "diastolic" && (
                <Line
                  type="linear"
                  dataKey="systolic"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot
                      {...props}
                      viewMode={viewMode}
                      getColorFn={getSystolicColor}
                    />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot {...props} getColorFn={getSystolicColor} />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              )}

              {/* Diastolic Line */}
              {viewMode !== "systolic" && (
                <Line
                  type="linear"
                  dataKey="diastolic"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot
                      {...props}
                      viewMode={viewMode}
                      getColorFn={getDiastolicColor}
                    />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot
                      {...props}
                      getColorFn={getDiastolicColor}
                    />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              )}
            </LineChart>
          </ResponsiveContainer>

          {/* Reference Range Labels */}
          {viewMode !== "total" && (
            <ReferenceRanges
              yDomain={yDomain}
              chartHeight={bloodPressureChartHeight}
              yMargin={yMargin}
              ranges={referenceRanges}
            />
          )}

          {/* Legend Labels */}
          {viewMode === "total" && (
            <>
              {/* Systolic Label */}
              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: systolicYPosition - 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: theme.palette.grey[700],
                  }}
                >
                  {t("HealthChart.bloodPressureChart.label.systolic")}
                </span>
              </div>
              {/* Diastolic Label */}
              <div
                style={{
                  position: "absolute",
                  right: 2,
                  top: diastolicYPosition - 28,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: theme.palette.grey[700],
                  }}
                >
                  {t("HealthChart.bloodPressureChart.label.diastolic")}
                </span>
              </div>
            </>
          )}
        </Box>

        {/* Selected Point Display */}
        <div style={{ position: "relative" }}>
          {/* Chevron indicator */}
          {activeIndex !== -1 && (
            <div
              style={{
                position: "absolute",
                left: getChevronPosition(
                  activeIndex,
                  data.length,
                  bloodPressureChartWidth
                ),
                top: -8,
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid ${theme.palette.grey[200]}`,
                zIndex: 10,
              }}
            />
          )}

          <Box
            sx={{
              padding: convertPxToRem(16),
              background: theme.palette.grey[200],
              borderRadius: convertPxToRem(10),
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.grey[700],
              }}
            >
              <span style={{ paddingRight: 3 }}>
                {t("HealthChart.bloodPressureChart.datePrefix")}
              </span>
              <span>{activePoint.fullDate}</span>
            </Typography>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "diastolic"
                      ? theme.palette.grey[300]
                      : theme.palette.grey[700],
                }}
              >
                {activePoint.systolic}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "total"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                }}
              >
                /
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "systolic"
                      ? theme.palette.grey[300]
                      : theme.palette.grey[700],
                }}
              >
                {activePoint.diastolic}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.bloodPressureChart.unit")}
              </span>
            </div>
          </Box>
        </div>
      </Box>
    </>
  );
};

export default BloodPressureChart;
